import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Sparkles, Play, UserCircle, BarChart3, Dumbbell, Users, Calendar, Dices,
  MessageSquare, Send, ShoppingCart, LucideIcon,
  AppWindow, Trophy,
} from 'lucide-react'

import { FlagName, isFeatureEnabled } from '../../../services/flagsService'
import { cn } from '@/lib/utils'

// Upcoming features announced on the home page. Every entry is always shown;
// `flag` records which feature flag launches it.
const UPCOMING: { flag: FlagName; key: string; icon: LucideIcon }[] = [
  { flag: 'USER_ACCOUNTS',           key: 'userAccounts',  icon: UserCircle },
  { flag: 'RATING_MENU',             key: 'ratings',       icon: BarChart3 },
  { flag: 'TRAINER_MODE',            key: 'trainer',       icon: Dumbbell },
  { flag: 'PROFILE_TEAMS',           key: 'teams',         icon: Users },
  { flag: 'PROFILE_EVENTS',          key: 'events',        icon: Calendar },
  // { flag: 'RANDOM_PACKAGE',          key: 'randomPackage', icon: Dices },
  { flag: 'QUESTION_ENGAGEMENT_BAR', key: 'engagement',    icon: MessageSquare },
  { flag: 'SUBMIT_PACKAGE',          key: 'submitPackage', icon: Send },
  { flag: 'MOBILE_APP',              key: 'mobileApp',     icon: AppWindow },
  { flag: 'TOURNAMENT_PANEL',        key: 'tournamentPanel', icon: Trophy },
  // { flag: 'BUZZER_SCORING',          key: 'scoring',       icon: ShoppingCart },
  // { flag: 'PACKAGE_ORDER',           key: 'packageOrder',  icon: ShoppingCart },
]

const SECONDS_PER_ITEM = 5
// Each scrolling copy repeats the list up to this many entries, so it stays wider
// than the viewport (no gap in the loop) even when only a few features are left.
const MIN_ITEMS_PER_COPY = 8

const ComingSoonTicker: React.FC = () => {
  const { t } = useTranslation()
  // Paused by tap/click, not :hover - on touch screens :hover sticks after a tap,
  // so a hover-based pause could never be undone by tapping again.
  const [paused, setPaused] = useState(false)
  const items = UPCOMING.filter(i => !isFeatureEnabled(i.flag))
  if (items.length === 0) return null
  const loop = Array.from({ length: Math.ceil(MIN_ITEMS_PER_COPY / items.length) }, () => items).flat()

  const renderItems = (hidden: boolean) => (
    <ul
      aria-hidden={hidden || undefined}
      className="flex shrink-0 items-center m-0 p-0 list-none"
    >
      {loop.map(({ key, icon: Icon }, i) => (
        <li
          key={`${key}-${i}`}
          // Repeats only exist to fill the loop: hide them from screen readers and
          // from the static reduced-motion list.
          aria-hidden={i >= items.length || undefined}
          className={cn(
            "flex items-center gap-2 px-5 whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300",
            i >= items.length && "motion-reduce:hidden"
          )}
        >
          <Icon className="w-4 h-4 text-blue-500 dark:text-indigo-300 flex-shrink-0" strokeWidth={2} />
          {t(`home.comingSoon.${key}`)}
          <span aria-hidden className="ml-5 w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-700" />
        </li>
      ))}
    </ul>
  )

  return (
    <section
      role="button"
      tabIndex={0}
      aria-pressed={paused}
      aria-label={t('home.comingSoon.label')}
      onClick={() => setPaused(p => !p)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setPaused(p => !p)
        }
      }}
      className="flex items-stretch overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm cursor-pointer select-none [-webkit-tap-highlight-color:transparent] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
    >
      <div className="flex items-center gap-2 px-4 py-3 flex-shrink-0 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        {paused ? (
          <Play className="w-3 h-3 fill-current motion-reduce:hidden" />
        ) : (
          <span className="relative flex w-2 h-2 motion-reduce:hidden">
            <span className="absolute inline-flex w-full h-full rounded-full bg-white/70 animate-ping" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-white" />
          </span>
        )}
        <Sparkles className="w-4 h-4 hidden sm:block" />
        <span className="text-xs font-bold uppercase tracking-wider">{t('home.comingSoon.label')}</span>
      </div>

      {/* Two identical copies scroll by -50% so the loop is seamless. Tap/click toggles
          pause; with reduced motion it becomes a plain horizontally scrollable list. */}
      <div className="relative flex-1 min-w-0 overflow-hidden motion-reduce:overflow-x-auto [mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)]">
        <div
          className={cn(
            "flex w-max py-3 animate-ticker",
            "motion-reduce:animate-none"
          )}
          style={{
            animationDuration: `${loop.length * SECONDS_PER_ITEM}s`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        >
          {renderItems(false)}
          <div className="contents motion-reduce:hidden">{renderItems(true)}</div>
        </div>
      </div>
    </section>
  )
}

export default ComingSoonTicker
